<template>
  <div id="package-base">
    <div
      class="sticky top-18 z-10 px-6 h-12 flex-shrink-0 flex items-center justify-between gap-y-2 gap-x-4 bg-pure-white border-b border-slate-400"
    >
      <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>

    <div class="py-6 px-6 w-full flex flex-col items-center">
      <div v-if="pageLoading" class="h-128 w-full grid place-items-center">
        <div class="blum-loader"></div>
      </div>
      <div v-else class="w-full max-w-180 flex flex-col gap-5">
        <div class="px-6 flex items-center justify-between gap-3">
          <BlTextTitle3
            :title="creditPackage.name"
            :text="creditPackage.name"
            class="truncate"
          />
          <div class="flex items-center gap-3">
            <BlInputButton
              type="outlined"
              label="Edit"
              @clicked="editOpened = true"
            />
            <BlInputButton
              v-if="creditPackage.status === 'archived'"
              label="Reactivate"
              :loading="submitLoading"
              @clicked="onReactivate"
            />
            <BlInputButton
              v-else
              type="outlined"
              label="Archive"
              @clicked="archiveOpened = true"
            />
          </div>
        </div>
        <div class="px-6 pt-2 flex gap-4 border-b border-slate-400">
          <router-link
            v-for="tab in tabs"
            :key="tab.target"
            :to="tab.target"
            class="py-1 w-full flex items-center justify-center rounded-t border-b-4 hover:bg-hover-state transition-all duration-300"
            :class="[
              tab.isActive ? 'border-light-purple' : 'border-transparent'
            ]"
          >
            <BlTextBody1
              :text="tab.label"
              :class="{ 'font-bold': tab.isActive }"
            />
          </router-link>
        </div>

        <PackageDetails v-if="!$route.params.tab" />
        <PackageUsageHistory v-if="$route.params.tab === 'history'" />
      </div>
    </div>

    <EditPackageModal
      v-if="editOpened"
      @close-modal="editOpened = false"
      :creditPackage="creditPackage"
    />

    <BlBackdrop
      v-if="archiveOpened"
      title="Archive package"
      label="Archive package"
      :submitLoading="submitLoading"
      @backdrop-close="archiveOpened = false"
      @backdrop-action="onArchive"
    >
      <div class="px-8" :style="{ width: '40rem' }">
        <div>
          <BlTextBody2 text="You are about to archive" />
          <BlTextBody2 :text="`${creditPackage.name}.`" class="font-bold" />
        </div>
        <div>
          <BlTextBody2
            text="Archiving this package will make it unavailable for new credit transaction. However, it will remain linked to any existing appointments or credits."
          />
        </div>
        <div>
          <BlTextBody2 text="Are you sure you want to proceed?" />
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayPrice } from "@/plugins/helper.js";

import PackageDetails from "./package/PackageDetails.vue";
import PackageUsageHistory from "./package/PackageUsageHistory.vue";
import EditPackageModal from "./package/EditPackageModal.vue";

import BlBreadcrumbs from "../BlBreadcrumbs.vue";
import BlTextTitle3 from "../BlTextTitle3.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import BlInputButton from "../BlInputButton.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "CenterPackage",
  components: {
    PackageDetails,
    PackageUsageHistory,
    EditPackageModal,
    //
    BlBreadcrumbs,
    BlTextTitle3,
    BlTextBody1,
    BlTextBody2,
    BlInputButton,
    BlBackdrop
  },
  data() {
    return {
      pageLoading: true,
      submitLoading: false,
      editOpened: false,
      archiveOpened: false
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "creditPackage"]),
    breadcrumbs() {
      const packageId = Number(this.$route.params.id);
      const packageLabel =
        (this.creditPackage && this.creditPackage.id) !== packageId
          ? `Package`
          : this.creditPackage.name;
      return [
        { label: "Center settings", target: `/center/package` },
        {
          label: packageLabel,
          target: `/center/package/${packageId}`,
          truncate: true
        }
      ];
    },
    tabs() {
      const packageId = Number(this.$route.params.id);
      return [
        {
          target: `/center/package/${packageId}`,
          isActive: !this.$route.params.tab,
          label: "Details"
        },
        {
          target: `/center/package/${packageId}/history`,
          isActive: this.$route.params.tab === "history",
          label: "Usage history"
        }
      ];
    }
  },
  async mounted() {
    if (!this.currentCenter.enable_appointment) {
      return this.$router.push({ name: "Center" });
    }

    const id = this.$route.params.id;

    this.pageLoading = true;
    await this.getCreditPackage({ id });
    this.pageLoading = false;
  },
  methods: {
    ...mapActions(["getCreditPackage", "updateCreditPackage"]),
    displayPrice,
    async onArchive() {
      const params = {
        id: this.creditPackage.id,
        package: { status: "archived" },
        title: "Package archived",
        message: `Success! The <b>${this.creditPackage.name}</b> has been archived from the center.`
      };

      this.submitLoading = true;
      const { success } = await this.updateCreditPackage(params);
      this.submitLoading = false;
      if (!success) return;
      this.archiveOpened = false;
    },
    async onReactivate() {
      const params = {
        id: this.creditPackage.id,
        package: { status: "active" },
        title: "Package activated",
        message: `Success! The <b>${this.creditPackage.name}</b> has been activated.`
      };

      this.submitLoading = true;
      await this.updateCreditPackage(params);
      this.submitLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/center/CenterPackage.vue